import React, { useEffect, useMemo } from 'react';
import {
  Box,
  Container,
  Grid,
  GridItem,
  Text,
  Link,
  HStack,
  Heading,
  Flex,
} from '@chakra-ui/react';
import { useParams, Link as RouterLink } from 'react-router-dom';
import { FiShield, FiArrowRight } from 'react-icons/fi';
import { getPlatform } from '../content/platforms';
import { useLegalLang } from '../LegalLangContext';
import { APP_META } from '../content/appMeta';
import PlatformHero from '../components/PlatformHero';
import TableOfContents from '../components/TableOfContents';
import { LegalSection, LegalParagraph, LegalList } from '../components/LegalSection';
import ScopeChips from '../components/ScopeChips';
import PlatformPolicyList from '../components/PlatformPolicyList';
import PlatformNotFound from '../components/PlatformNotFound';

const SECTION_KEYS = [
  's_acceptance',
  's_service',
  's_account',
  's_userResp',
  's_platformPolicy',
  's_dataRights',
  's_termination',
  's_disclaimer',
  's_changes',
  's_contact',
];

const TermsPage = ({ platformOverride }) => {
  const { platform: platformSlug } = useParams();
  const platform = getPlatform(platformOverride || platformSlug);
  const { t, lang } = useLegalLang();

  useEffect(() => {
    if (platform) {
      document.title = platformOverride
        ? `${t('termsDocTitle')} | ${APP_META.name}`
        : `${t('termsDocTitle')} — ${platform.name} | ${APP_META.name}`;
    } else {
      document.title = `${t('notFoundTitle')} | ${APP_META.name}`;
    }
  }, [platform, lang, t, platformOverride]);

  const tocItems = useMemo(
    () =>
      SECTION_KEYS.map(key => ({
        id: key.replace(/^s_/, 'sec-'),
        label: t(key),
      })),
    [t, lang],
  );

  if (!platform) {
    return <PlatformNotFound />;
  }

  const dataAccessed = platform.dataAccessed[lang] || platform.dataAccessed.en;
  const purposeText = platform.purpose[lang] || platform.purpose.en;

  return (
    <Box as="article">
      <PlatformHero platform={platform} docTitle={t('termsDocTitle')} />

      <Container maxW="6xl" px={{ base: 4, md: 6 }} pb={12}>
        <Grid templateColumns={{ base: '1fr', lg: '220px 1fr' }} gap={{ base: 4, lg: 10 }}>
          {/* TOC */}
          <GridItem display={{ base: 'none', lg: 'block' }}>
            <TableOfContents items={tocItems} />
          </GridItem>

          {/* Body */}
          <GridItem>
            <Box
              bg="surface.base"
              border="1px solid"
              borderColor="gray.200"
              borderRadius="xl"
              px={{ base: 5, md: 8 }}
              py={{ base: 6, md: 8 }}
              boxShadow="0 1px 0 rgba(0,0,0,0.02)"
            >
              {/* Intro */}
              <Box pb={2}>
                <LegalParagraph fontSize={{ base: 'md', md: 'lg' }} color="text.primary">
                  {t('termsIntroBeforePlatform')}{' '}
                  <Text as="span" fontWeight="700">
                    {platform.name}
                  </Text>{' '}
                  {t('termsIntroAfterPlatform')}
                </LegalParagraph>
              </Box>

              <LegalSection id="sec-acceptance" title={t('s_acceptance')}>
                <LegalParagraph>{t('s_acceptance_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-service" title={t('s_service')}>
                <LegalParagraph>{t('s_service_body_intro')}</LegalParagraph>

                <Heading
                  as="h3"
                  fontSize="sm"
                  fontWeight="700"
                  color="text.muted"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mt={5}
                  mb={2}
                >
                  {t('s_service_scopes_label')}
                </Heading>
                <ScopeChips
                  scopes={platform.scopes}
                  emptyLabel={
                    lang === 'vi'
                      ? 'Tích hợp này không dùng OAuth — sử dụng API token / cookie do bạn cung cấp.'
                      : 'This integration does not use OAuth — it uses an API token / cookie you provide.'
                  }
                />

                <Heading
                  as="h3"
                  fontSize="sm"
                  fontWeight="700"
                  color="text.muted"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mt={5}
                  mb={2}
                >
                  {t('s_service_data_label')}
                </Heading>
                <LegalList items={dataAccessed} />

                <Heading
                  as="h3"
                  fontSize="sm"
                  fontWeight="700"
                  color="text.muted"
                  textTransform="uppercase"
                  letterSpacing="wider"
                  mt={5}
                  mb={2}
                >
                  {t('s_service_purpose_label')}
                </Heading>
                <LegalParagraph>{purposeText}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-account" title={t('s_account')}>
                <LegalParagraph>{t('s_account_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-userResp" title={t('s_userResp')}>
                <LegalParagraph>{t('s_userResp_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-platformPolicy" title={t('s_platformPolicy')}>
                <LegalParagraph>{t('s_platformPolicy_body')}</LegalParagraph>
                <PlatformPolicyList links={platform.platformPolicyLinks} />
                {platform.extraNotes && (
                  <Box mt={4}>
                    {(platform.extraNotes[lang] || platform.extraNotes.en).map((note, i) => (
                      <LegalParagraph key={i} fontSize="sm" color="text.secondary">
                        {note}
                      </LegalParagraph>
                    ))}
                  </Box>
                )}
              </LegalSection>

              <LegalSection id="sec-dataRights" title={t('s_dataRights')}>
                <LegalParagraph>
                  {t('s_dataRights_body')}{' '}
                  <Link
                    as={RouterLink}
                    to={`/legal/${platform.slug}/privacy`}
                    color="brand.700"
                    fontWeight="600"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {t('privacyDocTitle')} — {platform.name}
                  </Link>
                  .
                </LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-termination" title={t('s_termination')}>
                <LegalParagraph>{t('s_termination_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-disclaimer" title={t('s_disclaimer')}>
                <LegalParagraph>{t('s_disclaimer_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-changes" title={t('s_changes')}>
                <LegalParagraph>{t('s_changes_body')}</LegalParagraph>
              </LegalSection>

              <LegalSection id="sec-contact" title={t('s_contact')}>
                <LegalParagraph>
                  {t('s_contact_body')}{' '}
                  <Link
                    href={`mailto:${APP_META.contactEmail}`}
                    color="brand.700"
                    fontWeight="600"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {APP_META.contactEmail}
                  </Link>
                  .
                </LegalParagraph>
              </LegalSection>
            </Box>

            {/* Cross-link to privacy */}
            <Box
              mt={6}
              p={5}
              bg="brand.50"
              border="1px solid"
              borderColor="brand.100"
              borderRadius="xl"
            >
              <HStack justify="space-between" align="center" flexWrap="wrap" gap={3}>
                <HStack spacing={3}>
                  <Flex
                    boxSize="40px"
                    borderRadius="10px"
                    bg="white"
                    color="brand.600"
                    align="center"
                    justify="center"
                  >
                    <FiShield size={18} />
                  </Flex>
                  <Box>
                    <Text fontWeight="700" color="text.primary">
                      {t('privacyDocTitle')} — {platform.name}
                    </Text>
                    <Text fontSize="sm" color="text.secondary">
                      {t('p_collected_intro')}
                    </Text>
                  </Box>
                </HStack>
                <Link
                  as={RouterLink}
                  to={`/legal/${platform.slug}/privacy`}
                  color="brand.700"
                  fontWeight="600"
                  display="inline-flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ textDecoration: 'underline' }}
                >
                  {t('viewPrivacy')} <FiArrowRight size={14} />
                </Link>
              </HStack>
            </Box>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
};

export default TermsPage;
